<template>
  <div class="timeline-point-wrapper">
    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <div
          v-for="idx in 3"
          :key="idx"
          class="point-section__list"
        >
          <div class="__numbering-wrapper">
            <b-skeleton type="avatar" />
          </div>
          <div class="point-detail">
            <div class="__address-wrapper">
              <b-skeleton width="100px" />
              <b-skeleton width="30px" />
            </div>
            <div class="__time-wrapper">
              <b-skeleton />
              <b-skeleton />
            </div>
            <div class="__badge-wrapper">
              <b-skeleton width="30px" />
            </div>
          </div>
        </div>
      </template>
      <div
        v-for="(point, index) in points"
        :key="point.id"
        class="point-section__list"
        :class="{ 'selected' : selectedPointIdx == index }"
        @click="showDetailSection(index)"
      >
        <div class="__numbering-wrapper">
          <div
            class="__index"
            :style="{
              backgroundColor: !['VISITING', 'VISITED'].includes(point.status.raw_type)
                ? '#67718B'
                : point.status.foreground_color,
            }"
          >
            <i
              v-if="!isDefaultType(point.type)"
              class="mc-icon"
              :class="{
                'icon-fill_flag': point.type === 'END',
                'icon-fill_home': point.type === 'START',
              }"
            />
            <template v-else>
              {{ isAnyStartPoint ? index : index + 1 }}
            </template>
          </div>
          <div class="__dashed-line" />
        </div>
    
        <!-- Point Details -->
        <div class="point-detail">
          <div class="__address-wrapper">
            <div class="__title">
              {{ point.address.name }}
            </div>
            <div
              class="__status"
              :style="{
                backgroundColor: point.status.background_color,
                color: point.status.foreground_color,
              }"
            >
              {{ computedStatus(point) }}
            </div>
          </div>
          <div class="__time-wrapper">
            <div v-if="isEndOrDefaultType(point.type)" class="__group">
              <span class="__label">{{
                isPointVisited(point.status.raw_type) ? $t('fleetTaskInstant.text.arrival') : $t('fleetTaskInstant.text.arrivalTarget')
              }}</span>
              <span class="__value">{{
                isPointVisited(point.status.raw_type)
                  ? formatDateWithTz(point.actual_time_arrival, 'HH:mm')
                  : formatDateWithTz(point.target_time_arrival, 'HH:mm')
              }}</span>
              <span
                v-if="isPointVisited(point.status.raw_type) && point.late_arrival_time"
                class="__late-value"
              >
                {{ formatLateValues(point.late_arrival_time) }}
              </span>
            </div>
            <div v-if="isStartOrDefaultType(point.type)" class="__group">
              <span class="__label">{{
                isPointVisited(point.status.raw_type) ? $t('fleetTaskInstant.text.departure') : $t('fleetTaskInstant.text.departureTarget')
              }}</span>
              <span class="__value">{{
                isPointVisited(point.status.raw_type)
                  ? formatDateWithTz(point.actual_time_departure, 'HH:mm')
                  : formatDateWithTz(point.target_time_departure, 'HH:mm')
              }}</span>
              <span
                v-if="isPointVisited(point.status.raw_type) && point.late_departure_time"
                class="__late-value"
              >
                {{ formatLateValues(point.late_departure_time) }}
              </span>
            </div>
          </div>
          <div v-if="point.service_time != null" class="__badge-wrapper">
            <div
              v-if="point.activity_label.raw_type !== ''"
              class="point-badge __activity-wrapper"
            >
              {{ $t(`fleetTaskInstant.text.${point.activity_label.raw_type.toLowerCase()}`) }}
            </div>
            <div class="point-badge __service-wrapper">
              <i class="mc-icon icon-outline_cube" />{{ formatValues(point.service_time) }}
            </div>
          </div>
        </div>
      </div>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatDateWithTz } from '@/helpers/tzDate'
import { formatValues, formatLateValues } from '../../../lib/helpers/formatValues'
import { statusIdToLabelMap } from '../../../lib/constants/point-status'

export default {
  props: {
    points: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: true,
    }
  },
  emits: ['show-detail-section'],
  data() {
    return {
      statusIdToLabelMap
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', [
      'selectedPointIdx',
    ]),
    isAnyStartPoint() {
      return this.points[0]?.type === 'START'
    },
    isLoading() {
      return this.loading
    },
    computedStatus() {
      return (item) => {
        const mappedStatus = this.statusIdToLabelMap[item.status?.raw_type]

        if (mappedStatus) {
          return this.$t(mappedStatus)
        }

        return item.status?.name
      }
    },
  },
  methods: {
    formatValues,
    formatDateWithTz,
    formatLateValues,

    isPointVisited(status) {
      return status === 'VISITED'
    },
    isEndOrDefaultType(value) {
      return ['END', 'DEFAULT'].includes(value)
    },
    isDefaultType(value) {
      return 'DEFAULT' === value
    },
    isStartOrDefaultType(value) {
      return ['START', 'DEFAULT'].includes(value)
    },

    showDetailSection(index) {
      this.$emit('show-detail-section', index)
    },
  }
}
</script>

<style lang="scss" scoped>
.timeline-point-wrapper {
  border: 1px solid $border-color;

  .point-section {
    &__list {
      display: flex;
      gap: 8px;
      align-items: stretch;
      padding: 0 4px;
      border-left: 3px solid transparent;
      cursor: pointer;

      &:first-child {
        .__numbering-wrapper>.__dashed-line {
          top: 10px;
        }
      }

      &:last-child {
        .__numbering-wrapper>.__dashed-line {
          min-height: 10px;
        }
      }

      .__numbering-wrapper {
        min-width: 20px;
        min-height: 100%;
        position: relative;
        margin: 0 10px;

        .__index {
          top: 10px;
          position: absolute;
          z-index: 2;
          min-width: 20px;
          height: 20px;
          border-radius: 20px;
          color: $white;
          background-color: $mep-color-text-300;
          font-size: 11px;
          text-align: center;
          display: flex;
          flex-direction: column;
          justify-content: center;

          &.visited {
            background-color: $mep-secondary;
          }

          .mc-icon::before {
            color: $white;
          }
        }

        .__dashed-line {
          position: absolute;
          min-height: 100%;
          width: 50%;
          border-right: 1px dashed #99a1b1;
        }
      }

      .point-detail {
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: 8px 0 12px 0;
        gap: 8px;

        .__address-wrapper {
          display: flex;
          justify-content: space-between;
          gap: 12px;

          .__title {
            font-weight: 600;
            color: $mep-primary;
            align-self: center;
          }

          .__status {
            min-width: fit-content;
            padding: 4px 8px;
            align-self: center;
            border-radius: 4px;

            font-size: 11px;
          }
        }

        .__time-wrapper {
          display: flex;
          flex-direction: column;
          font-size: 11px;

          .__group {
            display: flex;
            gap: 4px;

            .__label {
              color: $mep-color-text-300;
            }

            .__late-value {
              font-size: 10px;
              color: $red-400;
              font-style: italic;
            }
          }
        }

        .__badge-wrapper {
          display: flex;
          gap: 8px;

          .point-badge {
            display: flex;
            padding: 4px 8px;
            border-radius: 24px;
            border: 1px solid $mep-color-text-200;
            font-size: 11px;

            &.__service-wrapper {
              display: flex;
              gap: 4px;
              align-items: center;

              .mc-icon::before {
                font-size: 16px;
                color: $mep-color-text-200;
              }
            }
          }
        }
      }

      &:hover {
        background: $gray-100;
      }

      &.selected {
        border-left: 3px solid $mep-secondary;
      }
    }
  }
}
</style> 